import { Box, Grid, Stack, Title, Text, Group, Skeleton } from '@mantine/core'
import { IconStar } from '@tabler/icons-react'

import Head from 'next/head'
import Image from 'next/image'
import React, { useEffect, useMemo, useState } from 'react'
import useSWR from 'swr'
import ComingSoonCard from '../../components/coming_soon_card'
import Layout from '../../components/layout/index2.js'
import LiveStreamingCarousel from '../../components/livestreaming_carousel'
// import { useTranslations } from 'next-intl' // Eliminado
import GlobalAvailabilityMap from '../../components/services/global_availability_map'
import HitsCard from '../../components/services/hits_card'
import PlatformOverviewSection from '../../components/services/platform_overview'
import { useLocaleContext } from '../../context/locale'
import { searchAPI_v2 } from '../../utils/axios'
import countries from '../../utils/countries.json'
import Partnerships from '../../components/services/partnerships'
import TrafficShareTable from '../../components/services/traffic_share'

import ContactsTable from '../../components/services/contacts_table'
import PlansTable from '../../components/services/plans_table.js'

const fetcher = (url) => fetch(url).then((res) => res.json())

const Platform = ({ availablePlatforms }) => {
  const { country, setCountry } = useLocaleContext()
  const [liveStreaming] = useState('NO')
  const [isLoading, setIsLoading] = useState(true)

  // const t = useTranslations('Streaming Services Directory') // Eliminado

  const countryList = useSWR(
    availablePlatforms ? `/api/api_ext/platforms/${availablePlatforms.id}` : null,
    fetcher
  ).data

  useEffect(() => {
    if (!country) {
      const defaultCountries = ['us', 'ar', 'br', 'mx', 'gb', 'fr', 'de', 'it', 'es']
      const selectedCountry =
        defaultCountries.find((c) =>
          availablePlatforms.available_countries.includes(c.toLowerCase())
        ) || availablePlatforms.available_countries[0]
      setCountry(selectedCountry.toLowerCase())
    }
  }, [availablePlatforms, country, setCountry])

  const selectedCountryData = useMemo(
    () =>
      availablePlatforms.available_countries.find(
        (c) => c.toLowerCase() === country
      ),
    [availablePlatforms, country]
  )

  const platformId = availablePlatforms.id

  const urlMovie = `/api/api_ext/content/hits?country=${country}&type=movie&limit=10&platform=${platformId}`
  const urlSeries = `/api/api_ext/content/hits?country=${country}&type=series&limit=10&platform=${platformId}`

  const { data: movieData, isLoading: isLoadingMovies } = useSWR(urlMovie, fetcher)
  const { data: seriesData, isLoading: isLoadingSeries } = useSWR(urlSeries, fetcher)

  const combinedData = useMemo(() => {
    if (!movieData && !seriesData) return []

    const sortedMovies = movieData?.data?.sort((a, b) => b.hits - a.hits) || []
    const sortedSeries = seriesData?.data?.sort((a, b) => b.hits - a.hits) || []

    const combined = []
    for (let i = 0; i < Math.max(sortedMovies.length, sortedSeries.length); i++) {
      if (i < sortedMovies.length) combined.push(sortedMovies[i])
      if (i < sortedSeries.length) combined.push(sortedSeries[i])
    }

    return combined
  }, [movieData, seriesData])

  useEffect(() => {
    setIsLoading(isLoadingMovies || isLoadingSeries)
  }, [isLoadingMovies, isLoadingSeries])

  return (
    <>
      <Head>
        <title> {availablePlatforms.name} - Stream Discover</title>
        <meta
          name="description"
          content="Explore streaming platforms with comprehensive data and analysis."
        />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="icon" href="/favicon.ico" />
      </Head>
      <Stack
        sx={(theme) => ({
          width: '80vw',
          maxWidth: '1366px',
          position: 'relative',
          margin: '0 auto',
          [theme.fn.smallerThan('md')]: {
            width: '85vw'
          },
          [theme.fn.smallerThan('sm')]: {
            width: '90vw'
          }
        })}
      >
        <Box
          sx={(theme) => ({
            position: 'absolute',
            top: '-190px',
            right: '0',
            width: '130px',
            zIndex: 1,
            [theme.fn.smallerThan('xl')]: {
              right: '10%'
            },
            [theme.fn.smallerThan('lg')]: {
              right: '15%'
            },
            [theme.fn.smallerThan('md')]: {
              right: '20%'
            },
            [theme.fn.smallerThan('sm')]: {
              right: '25%'
            },
            [`@media (max-width: 700px)`]: {
              position: 'relative',
              top: -100,
              right: 'auto',
              margin: '0 auto',
              transform: 'none'
            }
          })}
        >
          <Image
            src={availablePlatforms.logo_url || '/logo.png'}
            width="130"
            height="130"
            alt="logo"
            priority
            style={{
              borderRadius: '10px',
              filter: 'brightness(1.1)'
            }}
          />
        </Box>

        <Box
          mt={64}
          sx={(theme) => ({
            position: 'absolute',
            top: '-228px',
            left: '0',
            textAlign: 'left',
            zIndex: 99,
            [theme.fn.smallerThan('xl')]: {
              left: '10%'
            },
            [theme.fn.smallerThan('lg')]: {
              left: '15%'
            },
            [theme.fn.smallerThan('md')]: {
              left: '20%'
            },
            [theme.fn.smallerThan('sm')]: {
              left: '25%'
            },
            [`@media (max-width: 700px)`]: {
              position: 'relative',
              top: -80,
              left: 'auto',
              transform: 'none',
              textAlign: 'center',
              margin: '0 auto 20px',
              width: '100%'
            }
          })}
        >
          <Title
            order={2}
            color="white"
            tt="uppercase"
            sx={{
              [`@media (max-width: 700px)`]: {
                fontSize: '1.5rem',
                whiteSpace: 'nowrap',
                overflow: 'visible'
              }
            }}
          >
            {availablePlatforms.name}
          </Title>
          <Text size="sm" color="dimmed" mb="xs">
            {availablePlatforms.owner}
          </Text>

          <Group
            spacing="xs"
            align="center"
            mt="md"
            sx={{
              justifyContent: 'flex-start',
              [`@media (max-width: 700px)`]: {
                justifyContent: 'center'
              }
            }}
          >
            {Array(5)
              .fill(0)
              .map((_, i) => (
                <IconStar
                  key={i}
                  size={20}
                  color={i < Number(availablePlatforms.stars) ? 'yellow' : 'gray'}
                  fill={i < Number(availablePlatforms.stars) ? 'yellow' : 'none'}
                />
              ))}
          </Group>
        </Box>

        <Box mt={24}>
          <PlatformOverviewSection
            country={country}
            setCountry={setCountry}
            selectedCountryData={selectedCountryData}
            availablePlatforms={availablePlatforms}
            countryList={countryList}
          />

          <Stack spacing="xl" my={72}>
            {liveStreaming === 'YES' && <LiveStreamingCarousel />}

            <Grid
              mb={70}
              gutter={26}
              sx={(theme) => ({
                '& > *': {
                  height: '613.5px'
                },
                [theme.fn.smallerThan('md')]: {
                  height: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '26px',
                  '& > *': {
                    width: '100%',
                    maxWidth: '100%',
                    height: '613.5px'
                  }
                }
              })}
            >
              {isLoading ? (
                <>
                  <Grid.Col span={4} sx={{ height: '613.5px' }}>
                    <Skeleton height="100%" radius="md" />
                  </Grid.Col>
                  <Grid.Col span={8} sx={{ height: '613.5px' }}>
                    <Skeleton height="100%" radius="md" />
                  </Grid.Col>
                </>
              ) : (
                <>
                  {combinedData.length > 0 && (
                    <HitsCard
                      combinedData={combinedData}
                      availablePlatforms={availablePlatforms}
                      selectedCountryData={selectedCountryData}
                      countries={countries}
                      sx={{ height: '613.5px' }}
                    />
                  )}
                  <GlobalAvailabilityMap
                    availablePlatforms={availablePlatforms}
                    setCountry={setCountry}
                    name={availablePlatforms.name}
                    sx={{ height: '613.5px' }}
                  />
                </>
              )}
            </Grid>

            {availablePlatforms.partners?.length > 0 && (
              <Partnerships
                partners={availablePlatforms.partners}
                name={availablePlatforms.name}
                platformId={platformId}
              />
            )}

            {availablePlatforms.visitors_share?.length > 0 && (
              <TrafficShareTable
                visitors={availablePlatforms.visitors_share}
                name={availablePlatforms.name}
                platformId={platformId}
              />
            )}

            {availablePlatforms.contact_details?.length > 0 && (
              <ContactsTable
                contacts={availablePlatforms.contact_details}
                name={availablePlatforms.name}
                platformId={platformId}
              />
            )}

            {availablePlatforms.prices?.length > 0 && (
              <PlansTable
                prices={availablePlatforms.prices}
                name={availablePlatforms.name}
                platformId={platformId}
              />
            )}

            <ComingSoonCard
              platformId={platformId}
              country={country}
              name={availablePlatforms.name}
            />
          </Stack>
        </Box>
      </Stack>
    </>
  )
}

export default Platform

Platform.getLayout = function getLayout(page) {
  return (
    <Layout withAutocomplete showHero={false}>
      {page}
    </Layout>
  )
}

export async function getServerSideProps({ params }) {
  try {
    const [id] = params.platform.split('-')

    let availablePlatformsSSD = null
    let availablePlatforms = null

    try {
      const { data } = await searchAPI_v2(`/platforms/ssd/${id}`)
      availablePlatformsSSD = data
    } catch (error) {
      console.warn(`Error fetching from /platforms/ssd/${id}:`, error.message)
    }

    try {
      const { data } = await searchAPI_v2(`/platforms/${id}`)
      availablePlatforms = data
    } catch (error) {
      console.error(`Error fetching from /platforms/${id}:`, error.message)
      throw new Error('No se pudo obtener datos del endpoint original.')
    }

    if (availablePlatformsSSD) {
      availablePlatforms = {
        ...availablePlatforms,
        ...availablePlatformsSSD
      }
    }

    return {
      props: {
        availablePlatforms
      }
    }
  } catch (error) {
    console.error(`Error en getServerSideProps: ${error.message}`)
    return { props: {} }
  }
}
